import { getGeocodeByAddress } from '../../apis/backend';
import { updateShopData } from '../../apis/shop.service';
import { formatAddress, isValidAddress } from '../../utils/shopUtils';
import { KEYWORDS } from '../../constants';
import { FormattedAddress, LegalInfo, ShopInfo, Configurations, ChosenTechs } from './LaunchingForm.types';

export const removeTypedAddress = <T extends { typedAddress?: string }>(obj: T): Omit<T, 'typedAddress'> => {
  const { typedAddress, ...rest } = obj;
  return rest;
};

export const geocodeAddress = async (
  addressInput: string,
  includeLocation = false
): Promise<FormattedAddress | null> => {
  try {
    const geoCodeAddress = await getGeocodeByAddress(addressInput);
    const arrayAddress = geoCodeAddress[0]?.address_components;

    if (!arrayAddress || isValidAddress(arrayAddress)) {
      return null;
    }

    const formattedAddress = formatAddress(arrayAddress);
    
    if (includeLocation && geoCodeAddress[0]?.geometry?.location) {
      return {
        ...formattedAddress,
        location: {
          latitude: geoCodeAddress[0].geometry.location.lat,
          longitude: geoCodeAddress[0].geometry.location.lng,
        },
      };
    }

    return formattedAddress;
  } catch (error) {
    console.error('Error geocoding address:', error);
    throw new Error('Failed to geocode address');
  }
};

export const updateShopDataService = async (
  legal: LegalInfo,
  shop: ShopInfo,
  configurations: Configurations,
  chosenTechs: ChosenTechs
): Promise<void> => {
  try {
    await updateShopData(legal, shop, configurations, chosenTechs);
  } catch (error) {
    console.error('Error updating shop data:', error);
    throw new Error('Failed to update shop data');
  }
};

export interface LaunchingFormServices {
  geocodeAddress: typeof geocodeAddress;
  updateShopData: typeof updateShopDataService;
}

export const normalizeVatNumber = (value: string): string => {
  return value.trim().toUpperCase();
};

export const validatePhoneNumber = (
  value: string,
  countryCode: { dialCode: string; iso2: string },
  phoneNumberMinLength: (iso2: string) => number
): boolean => {
  const { dialCode, iso2 } = countryCode;
  const minLength = phoneNumberMinLength(iso2);
  
  const digitsOnly = value.replace(/\D/g, '');
  
  if (!digitsOnly || digitsOnly.length === 0) {
    return true;
  }
  
  if (digitsOnly.startsWith(dialCode)) {
    const withoutCountryCode = digitsOnly.substring(dialCode.length);
    const isIncorrect = withoutCountryCode.length < minLength || withoutCountryCode[0] === '0';
    return Boolean(isIncorrect);
  }
  
  return digitsOnly.length < minLength;
};

export const buildKeywords = (
  currentKeyWords: string,
  shopName?: string,
  companyName?: string,
  businessType?: string,
  shopAddress?: FormattedAddress | null
): string => {
  let keyWordsToReturn = currentKeyWords;

  if (shopName) {
    keyWordsToReturn = `${shopName}, ${keyWordsToReturn.replace(`${shopName}, `, '')}`;
  }

  if (companyName) {
    keyWordsToReturn = `${companyName}, ${keyWordsToReturn.replace(`${companyName}, `, '')}`;
  }

  if (businessType) {
    const previousKeyword = KEYWORDS[businessType as keyof typeof KEYWORDS];
    keyWordsToReturn = keyWordsToReturn.replace(`${previousKeyword}, `, '');
    keyWordsToReturn = `${KEYWORDS[businessType as keyof typeof KEYWORDS]}, ${keyWordsToReturn}`;
  }

  if (shopAddress) {
    const city = shopAddress.city || '';
    const street = shopAddress.street || '';
    keyWordsToReturn = keyWordsToReturn.replace(`${city}, `, '').replace(`${street}, `, '');
    keyWordsToReturn = `${shopAddress.streetNumber} ${shopAddress.street}, ${shopAddress.city}, ${shopAddress.country} ${keyWordsToReturn}`;
  }

  return keyWordsToReturn;
};

export const defaultLaunchingFormServices: LaunchingFormServices = {
  geocodeAddress,
  updateShopData: updateShopDataService,
};

